/**
 * Real-user page speed (Core Web Vitals), measured in the browser with the standard
 * PerformanceObserver API — no library — and sent once when the page is hidden or closed.
 *
 * Off until `environment.webVitalsUrl` is set: the backend endpoint that receives these
 * reports does not exist yet. Call from an app initializer; it does nothing on the server.
 *
 * Reported values, all in milliseconds except CLS (unitless):
 *   lcp  Largest Contentful Paint — when the main content appeared
 *   fcp  First Contentful Paint   — when anything first appeared
 *   ttfb Time to First Byte       — server response time
 *   cls  Cumulative Layout Shift  — how much the page jumped while loading
 *   inp  Interaction to Next Paint (approximation) — slowest response to a click or key press
 */
export interface WebVitalsReport {
  app: string;
  path: string;
  lcp?: number;
  fcp?: number;
  ttfb?: number;
  cls: number;
  inp?: number;
}

export function startWebVitals(app: string, endpoint: string | undefined): void {
  if (!endpoint || typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    return;
  }

  const report: WebVitalsReport = { app, path: location.pathname, cls: 0 };

  const observe = (type: string, onEntry: (entry: PerformanceEntry) => void, extra = {}) => {
    try {
      new PerformanceObserver((list) => list.getEntries().forEach(onEntry)).observe({
        type,
        buffered: true,
        ...extra,
      });
    } catch {
      // This browser doesn't support the entry type; leave that metric out.
    }
  };

  observe('largest-contentful-paint', (entry) => (report.lcp = Math.round(entry.startTime)));
  observe('paint', (entry) => {
    if (entry.name === 'first-contentful-paint') report.fcp = Math.round(entry.startTime);
  });
  observe('layout-shift', (entry) => {
    const shift = entry as PerformanceEntry & { value: number; hadRecentInput: boolean };
    if (!shift.hadRecentInput) report.cls = Math.round((report.cls + shift.value) * 1000) / 1000;
  });
  observe(
    'event',
    (entry) => (report.inp = Math.max(report.inp ?? 0, Math.round(entry.duration))),
    { durationThreshold: 40 },
  );

  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (navigation) report.ttfb = Math.round(navigation.responseStart);

  let sent = false;
  const send = () => {
    if (sent || document.visibilityState !== 'hidden') return;
    sent = true;
    const body = JSON.stringify(report);
    if (!navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'application/json' }))) {
      void fetch(endpoint, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => undefined);
    }
  };
  document.addEventListener('visibilitychange', send);
  window.addEventListener('pagehide', send);
}
